import { useEffect, useRef, useState } from 'react';
import { scanBusinessCard } from '../lib/api';
import { iconForCategory } from '../lib/categoryGrid';
import { type CloseField, isValidEmailShape } from '../lib/contactCommit';
import type { ContactFieldMap, ContactFieldName } from '../lib/contactFields';
import {
  type FieldSubmit,
  IDLE_SUBMIT,
  type SubmitAckState,
  decideEmailSubmit,
  decidePhoneSubmit,
  reconcileSubmitAck,
  resolveSubmitAck,
  submitAckLabel,
} from '../lib/contactSubmit';
import { displayPieceName } from '../lib/format';
import type { OrderLine } from '../lib/orderReview';
import { orderTotalUnits } from '../lib/orderReview';
import type { SendState } from '../lib/sendState';
import { CardScanSource } from './CardScanSource';
import { Icon } from './Icon';
import { NoImageFill } from './NoImageFill';
import { OnStandBadge } from './OnStandBadge';

/**
 * S7 Close scene (R12 field-fix F1/F3 — the post-order close card).
 *
 * Field evidence (dev-73, 2026-07-08 turns 21–24): after Confirm, Gigi's email capture called the
 * retired `open_contact_form` path → nothing rendered ("I don't see it"); she fell back to voicing
 * the email (violates the hard rule). Separately (user report #2) the spec-sheet screen "flashed
 * then reverted" — a post-commit `ui.scene {browse}` stomped the confirmation.
 *
 * R12 makes 'close' a real, terminal scene of the showcase state machine (lib/scene.ts):
 *   - Order recap (read-only — reuses the S6 line shape) so the visitor sees exactly what they
 *     confirmed.
 *   - KEYBOARD-FIRST email field (STT on emails is unreliable — §4.2 never voice-fills email) +
 *     the reusable business-card scan.
 *   - Sent/queued state, driven by `closeEmailAck` from the server (`ui.contact_ack`):
 *       ack === true  → "Your spec sheet is on its way ✓"
 *       ack === false → "We've got your details — we'll email it as soon as sending is live."
 *                        (send is deferred until Resend is provisioned — delivery:'queued'.)
 *       ack === null  → in-flight ("Sending…").
 *
 * STOMP-PROOF: the scene *container* (ShowcaseScene) refuses to leave 'close' for a late product
 * render (F3) — this component just owns the close presentation. Tokens only, day+night,
 * reduced-motion safe via the .reveal animation.
 */

type Props = {
  /** The confirmed order lines (read-only recap). */
  lines: OrderLine[];
  /**
   * R13 U4 — the live contact-field map (name/company/phone), so a card-scan `phone` (merged into
   * the store) pre-fills the optional phone field, and a voiced name/company is reflected here too.
   */
  fields: ContactFieldMap;
  /** Submit the close email/details (reuses submit-contact → ui.contact_ack via VoiceDemo). */
  onSubmitEmail: (fields: {
    email?: string;
    name?: string;
    company?: string;
    phone?: string;
  }) => void;
  /** Hand-typed edit → typed final in the store (so a typed phone survives a later card echo). */
  onType: (field: ContactFieldName, value: string) => void;
  /** Card scan can also fill the close card (name/company/email/phone). */
  onCardScan: (fields: {
    name?: string;
    company?: string;
    email?: string;
    phone?: string;
  }) => void;
  /** Server ack for the submitted email: null = in-flight, true = sent/queued, false = deferred. */
  ack: boolean | null;
  /**
   * R14 B3 — the richer spec-sheet send state (null | 'sent' | 'queued' | 'no_order' | 'error').
   * When 'no_order' arrives on a send attempt (no committed order this session) the card shows
   * "confirm your selection first" — never a false "on its way". Falls back to `ack` when absent.
   */
  sendState?: SendState;
  /** R14 B3 — back-to-review affordance for the no_order state (return to S6 to confirm). */
  onBackToReview?: () => void;
  /** Local touch to leave the close card (Done → the S9 end scene). Always allowed. */
  onDone: () => void;
  conversationId?: string;
  /** R25 — scan surface open/close → host mutes the mic + emits the §38 `card-modal` signal. */
  onScanSurfaceChange?: (active: boolean) => void;
  /**
   * R30 V1 — emit the DEBOUNCED §40.4b `composer-draft` signal while a field is being typed (holds
   * the server's send/wind-down timeout; surfaces the typed-but-uncommitted value). `field` is the
   * S7 field ('email'|'phone'); `value` the in-progress text. Fires on change; the host debounces.
   */
  onDraftChange?: (field: 'email' | 'phone', value: string) => void;
  /** R30 V1 — stop the draft signal on blur/submit/empty (cancels the pending debounced emit). */
  onDraftStop?: () => void;
  /**
   * R30 V2 — the FOCUS-GUARD hook. `true` when a field is focused with a non-empty draft, `false`
   * when it blurs / empties. While active, the host refuses to let a server scene change replace the
   * close card (the dev-117 glitch: a `ui.scene:'end'` swapped the card for the walkthrough while the
   * visitor typed her email). Client-local; independent of server emissions.
   */
  onFieldGuardChange?: (active: boolean) => void;
};

export function CloseScene({
  lines,
  fields,
  onSubmitEmail,
  onType,
  onCardScan,
  ack,
  sendState,
  onBackToReview,
  onDone,
  conversationId,
  onScanSurfaceChange,
  onDraftChange,
  onDraftStop,
  onFieldGuardChange,
}: Props) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [scanning, setScanning] = useState(false);
  // R30 V2 — which field currently holds focus (null = none). The guard is (focused ∧ draft-non-empty).
  const [focusedField, setFocusedField] = useState<'email' | 'phone' | null>(null);
  // R32 X1/X2 — the PERSISTENT per-field submit-ack (replaces R31's transient `.done-acked` flash).
  // Each record carries the live ack state (idle|pending|saved|error) + the exact value it describes;
  // Saved HOLDS until the field VALUE CHANGES (reconcileSubmitAck) — a reverting tick reads as "it
  // didn't take" on a kiosk. Resolved truthfully by the server ack (resolveSubmitAck).
  const [emailSubmit, setEmailSubmit] = useState<FieldSubmit>(IDLE_SUBMIT);
  const [phoneSubmit, setPhoneSubmit] = useState<FieldSubmit>(IDLE_SUBMIT);
  // R31 W2 — `hint` carries a gentle per-field message when a Done fails validation (never silence).
  const [hint, setHint] = useState<{ field: CloseField; text: string } | null>(null);
  // R32 X3 — once the email is Saved, pulse the Send pill so a visitor who never taps Send SEES the
  // path forward (the dev-119 hang: she filled the box but the flow never surfaced the next action).
  const [pathForwardCued, setPathForwardCued] = useState(false);
  // Refs so a Done submit can advance focus (email → phone) or surface the Send CTA.
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const sendBtnRef = useRef<HTMLButtonElement>(null);

  // R14 B3 — resolve the effective send state: prefer the richer `sendState` (carries no_order);
  // fall back to the legacy `ack` boolean (true→sent, false→queued, null→in-flight) so the card
  // still works before the R31 server half freezes the state string.
  const resolved: SendState =
    sendState !== undefined ? sendState : ack === null ? null : ack ? 'sent' : 'queued';
  const noOrder = resolved === 'no_order';

  // R32 X1 — a genuine SEND was attempted iff the visitor tapped the Send pill (`submitted`). A
  // contact-only Done submit (X1) also produces a `ui.contact_ack {persisted}` → a sent/queued
  // sendState, but it must NOT flip the whole card to the "on its way" sent screen — it only resolves
  // the field's persistent Saved chip (X2). So the sent-screen gate below no longer fires on a bare
  // send state alone; it fires on a local Send tap OR the server driving `ui.scene:'end'` (R58 D1's
  // mechanical send + the M5 walkthrough — the SERVER-driven path, cases (c)/(d) below). This is the
  // dev-119 fix: a Done that puts the email on file no longer masquerades as a completed send.
  const sent = resolved === 'sent' || resolved === 'queued';
  // Only the visitor's own Send tap shows the sent beat locally; the voice/mechanical send now lands
  // its beat via the server `ui.scene:'end'` (R22 K1 c/d). A pending Done-submit awaiting its ack
  // therefore keeps the form on screen — the Saved chip resolves in place, the card holds.
  const showSent = submitted;

  // R22 K1 — 🔴 THE S7 CLOSE CARD HOLDS. USER-SPECIFIED (Meera turns 40–45): the S7 close card is THE
  // contact-capture home and must NOT auto-advance to the end scene ON ITS OWN. It leaves ONLY on:
  //   (a) the visitor SUBMITTING / tapping the card's continue (a LOCAL submit → `submitted`);
  //   (b) an explicit skip affordance (the local Done peer, `onDone`);
  //   (c) the server advancing after a REAL sent/queued-WITH-CONTACT ack (`ui.scene:'end'`);
  //   (d) the visitor telling Gigi aloud they've filled it (server-driven `ui.scene:'end'`).
  //
  // WAS (R14 B3 / R16 V4): a client-side ~2.4s timer fired on ANY sent/queued ack — INCLUDING a
  // queued/no-contact state (delivery deferred, nothing submitted) and a voice-driven send the visitor
  // never touched. That is the "keeps skipping" bug: the card yielded to the goodbye before Meera could
  // type. NOW the client auto-advance fires ONLY when the visitor SUBMITTED on THIS card (`submitted`
  // — set in handleSend, which requires a non-empty email, so contact is guaranteed present) AND the
  // send genuinely resolved (sent/queued). A pure server ack with no local submit (the voice-driven
  // send / a spoken "I've filled it in") does NOT arm the client timer — cases (c)/(d) are
  // SERVER-DRIVEN: concierge R42 (T1/§29) gates the `ui.scene:'end'` emission on
  // `contact-submitted ∨ explicitly-skipped ∨ sheet-actually-sent`, so the scene change lands from the
  // server when — and only when — contact is truly on file. A no_order / error / in-flight state never
  // auto-advances. Reduced-motion safe (a timer, not motion).
  const submittedSend = submitted && sent;
  useEffect(() => {
    if (!submittedSend) return;
    const t = setTimeout(() => onDone(), 2400);
    return () => clearTimeout(t);
  }, [submittedSend, onDone]);

  // R13 U4 — the optional phone field is store-backed (so a card-scan phone pre-fills it and a
  // typed phone becomes a typed-final). Kept local for the input, mirrored to the store on change.
  const phone = fields.phone?.value ?? '';

  // R32 X2 — reconcile the phone's persistent ack against its (store-backed) current value: Saved
  // HOLDS while unchanged, reverts to idle on a change (a card-scan echo that changes the value also
  // reverts, correctly — the Saved described the old value).
  useEffect(() => {
    setPhoneSubmit((r) => reconcileSubmitAck(r, phone));
  }, [phone]);

  // R30 V2 — the FOCUS-GUARD is active iff a field is focused AND its draft is non-empty. It holds
  // the close card against a server scene swap (dev-117). Derived from focus + the two field values,
  // reported to the host through an effect so the store flag tracks it (and is cleared on unmount).
  const draftForField = focusedField === 'email' ? email : focusedField === 'phone' ? phone : '';
  const guardActive = focusedField !== null && draftForField.trim() !== '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: report on the derived guard boolean only.
  useEffect(() => {
    onFieldGuardChange?.(guardActive);
  }, [guardActive]);
  // Clear the guard + stop any pending draft on unmount (the scene left / session reset), so a stale
  // guard never traps the NEXT visitor on 'close' and no orphaned draft emit fires.
  // biome-ignore lint/correctness/useExhaustiveDependencies: unmount-only cleanup.
  useEffect(() => {
    return () => {
      onFieldGuardChange?.(false);
      onDraftStop?.();
    };
  }, []);

  // R32 X2 — resolve a PENDING Done-submit when the server contact ack lands. The ack (`ack` prop,
  // set from `ui.contact_ack {persisted}`) is session-wide (it doesn't name the field), so it resolves
  // whichever field(s) are 'pending' at their submitted value. `persisted:true` → Saved; a genuine
  // error state → error (never a false Saved). A queued/deferred SEND is still a persisted CONTACT
  // (the lead row is written independent of spec-sheet delivery) → truthful Saved. A no_order state is
  // an order problem, not a contact-persistence failure — it leaves the Saved chip alone (the email
  // IS on file) and surfaces via the separate no_order screen. Idempotent: a 'saved' record ignores
  // repeat acks. UNVERIFIED binding — cross-verify the R58 D2 ack shape at integration.
  useEffect(() => {
    if (ack === null && sendState === undefined) return; // in-flight / nothing acked yet.
    if (resolved === 'no_order') return; // an order problem — contact persistence is unaffected.
    const isError = resolved === 'error';
    const persisted = ack === true || resolved === 'sent' || resolved === 'queued';
    if (!isError && !persisted) return; // an ack we can't read as persisted-or-failed — hold.
    setEmailSubmit((r) => resolveSubmitAck(r, { persisted, error: isError }));
    setPhoneSubmit((r) => resolveSubmitAck(r, { persisted, error: isError }));
  }, [ack, sendState, resolved]);

  // R32 X2 — reconcile each field's persistent ack against the CURRENT value on every value change:
  // Saved HOLDS while the box still holds the submitted value; a change reverts to 'idle' (a stale
  // Saved would be a lie). The phone reconciles against the store-backed value below (declared later),
  // so it runs in its own effect once `phone` is in scope.
  useEffect(() => {
    setEmailSubmit((r) => reconcileSubmitAck(r, email));
  }, [email]);

  // R30 V1 — a field edit: mirror to the store (existing behaviour) AND emit the debounced draft
  // signal. An empty value stops the signal (never emit an empty draft). `field` is the S7 field.
  // R31 W2 — an edit also clears any stale hint for that field (the visitor is fixing it). R32 X2 —
  // the persistent Saved chip reverts on a value change via `reconcileSubmitAck` (the effects above),
  // not here, so a re-typed-identical value keeps Saved.
  function handleDraft(field: 'email' | 'phone', value: string) {
    if (hint?.field === field) setHint(null);
    if (value.trim() === '') {
      onDraftStop?.();
    } else {
      onDraftChange?.(field, value);
    }
  }
  // R30 V1/V2 — a field blur: stop the draft signal (the pending emit is cancelled) and drop focus so
  // the guard releases. Submit calls this too (via handleSend) so the guard never outlives the entry.
  function handleFieldBlur() {
    setFocusedField(null);
    onDraftStop?.();
  }

  // R32 X1 — advance the DOM after a Done resolves (blur settled). Reuses the R31 advance vocabulary;
  // 'send' means the phone leg completed the contact with a valid email — but on the Done path we do
  // NOT auto-send the spec sheet (Done is contact-only; the Send pill / the server's mechanical send
  // owns the send). So 'send' from a phone Done SURFACES the Send pill (X3) rather than tapping it —
  // the visitor's contact is on file; the send stays their explicit choice / the server's job.
  function applyAdvance(advance: 'focus-phone' | 'surface-send' | 'send' | 'none' | '') {
    switch (advance) {
      case 'focus-phone':
        phoneInputRef.current?.focus();
        break;
      case 'surface-send':
      case 'send':
        surfaceSendPill();
        break;
    }
  }

  // R32 X3 — surface the path forward: scroll the Send pill into view, pulse it (the `send-cue`
  // auto-settling brass-ring breathe — the attention-breathe family, §8), and focus it, so a visitor
  // who has Saved their email but never taps Send still SEES the flow move.
  function surfaceSendPill() {
    setPathForwardCued(true);
    sendBtnRef.current?.scrollIntoView({ block: 'nearest' });
    sendBtnRef.current?.focus();
  }

  // R32 X1 — Done-as-SUBMIT. Extends R31's Done-as-commit: a valid email now SUBMITS to the server
  // (via the existing `submit-contact` wire — the R58 D2 seam — so `leads.email` becomes real and the
  // mechanical send has an address), goes into a PERSISTENT 'pending' → Saved state (X2), blurs, and
  // surfaces the path forward (X3). Contact-ONLY: Done never shows the "on its way" send screen — that
  // is the Send pill's / the server's job. Invalid → the R31 inline hint (never silence). An empty
  // optional phone Done just advances (nothing to post). Re-tapping Done on the SAME value re-acks
  // without re-posting (client-side idempotency — `resubmit`). The decision is the pure
  // `decideEmailSubmit`/`decidePhoneSubmit`; this owns the DOM + network side-effects.
  function handleFieldSubmit(field: CloseField) {
    const decision =
      field === 'email'
        ? decideEmailSubmit(email, {
            phoneFilled: phone.trim() !== '',
            lastSubmitted: emailSubmit.submittedValue,
          })
        : decidePhoneSubmit(phone, {
            emailValid: isValidEmailShape(email),
            lastSubmitted: phoneSubmit.submittedValue,
          });

    if (decision.action === 'hint') {
      // Gentle inline hint; hold focus so the visitor can fix it right where they are.
      setHint({ field, text: decision.hint });
      return;
    }
    // Valid → clear any hint, blur (dismisses the kiosk keyboard; the R30 guard releases), then
    // submit-or-skip + advance.
    setHint((h) => (h?.field === field ? null : h));
    const input = field === 'email' ? emailInputRef.current : phoneInputRef.current;
    input?.blur();
    handleFieldBlur();

    if (decision.action === 'submit') {
      // POST the contact (email required by validation; phone carried only if present). Contact-only —
      // this does NOT flip `submitted` (no sent screen). Mark the field 'pending' at its exact value;
      // the server `ui.contact_ack` resolves it to Saved (X2, the effect above).
      submitContactOnly();
      const submittedValue = (field === 'email' ? email : phone).trim();
      if (field === 'email') setEmailSubmit({ state: 'pending', submittedValue });
      else setPhoneSubmit({ state: 'pending', submittedValue });
    } else if (decision.action === 'resubmit') {
      // Idempotent re-ack: the value is already on file — re-affirm Saved WITHOUT a re-post. Optimistic
      // (the record's submittedValue is unchanged; keep/restore Saved so the chip reads truthfully).
      const submittedValue = (field === 'email' ? email : phone).trim();
      if (field === 'email') setEmailSubmit({ state: 'saved', submittedValue });
      else setPhoneSubmit({ state: 'saved', submittedValue });
    }
    // 'skip' (empty optional phone) → nothing to post; just advance.
    applyAdvance(decision.advance);
  }

  // R32 X1 — the contact-only submit: reuses the existing `onSubmitEmail` (→ `submit-contact` wire,
  // R58 D2 seam). Sends only the fields present (email always, phone if typed) — never fabricates,
  // never an empty string (the pure `buildContactFormPayload` on the useVoice side drops empties).
  function submitContactOnly() {
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    onSubmitEmail({
      ...(trimmedEmail ? { email: trimmedEmail } : {}),
      ...(trimmedPhone ? { phone: trimmedPhone } : {}),
    });
    // A submit is a COMMIT: release the focus-guard + stop the draft signal (the value is now going on
    // file; the server may advance freely — the visitor is no longer mid-entry).
    setFocusedField(null);
    onFieldGuardChange?.(false);
    onDraftStop?.();
  }

  async function handleFile(file: File) {
    setScanning(true);
    try {
      const result = await scanBusinessCard(file, conversationId);
      if (result.fields.email) setEmail(result.fields.email);
      onCardScan({
        name: result.fields.name,
        company: result.fields.company,
        email: result.fields.email,
        phone: result.fields.phone,
      });
    } catch {
      // silent — the visitor can still type
    } finally {
      setScanning(false);
    }
  }

  function handleSend(e?: React.FormEvent) {
    e?.preventDefault();
    if (email.trim() === '') return;
    // Phone is optional — included only if present (never fabricate / never send an empty string).
    const trimmedPhone = phone.trim();
    onSubmitEmail({ email: email.trim(), ...(trimmedPhone ? { phone: trimmedPhone } : {}) });
    setSubmitted(true);
    // R32 X2 — the Send also puts the email on file: mark it 'pending' so the persistent Saved state
    // stays consistent with the sent screen (a full send is a superset of the Done submit).
    setEmailSubmit({ state: 'pending', submittedValue: email.trim() });
    if (trimmedPhone) setPhoneSubmit({ state: 'pending', submittedValue: trimmedPhone });
    // R30 V1/V2 — submit is a COMMIT: release the focus-guard + stop the draft signal (the value is
    // now on file; the server may advance freely — the visitor is no longer mid-entry).
    setFocusedField(null);
    onFieldGuardChange?.(false);
    onDraftStop?.();
  }

  // R14 B3 — no_order guard: the server refused the send because no order was committed this
  // session. NEVER show "on its way". Show "confirm your selection first" with a back-to-review
  // affordance so the visitor can complete the order. This branch is reachable whether or not the
  // visitor tapped Send (a voiced send with no order also lands here).
  if (noOrder) {
    return (
      <div
        className="reveal flex flex-col items-center gap-4 py-8 text-center"
        data-testid="close-no-order"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-fill ring-1 ring-line">
          <Icon name="alertTriangle" size={22} className="text-subtle" />
        </span>
        <p className="font-display text-lg text-ink" data-testid="close-no-order-headline">
          Let&apos;s confirm your selection first
        </p>
        <p className="max-w-xs text-sm text-subtle leading-relaxed">
          I don&apos;t have a confirmed order yet, so there&apos;s nothing to send. Pop back to your
          selection, confirm it, and I&apos;ll email your spec sheet across.
        </p>
        <button
          type="button"
          data-testid="close-back-to-review-btn"
          onClick={() => {
            setSubmitted(false);
            onBackToReview?.();
          }}
          className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-display"
        >
          <Icon name="package" size={14} />
          Review my selection
        </button>
      </div>
    );
  }

  // Sent / queued acknowledgment. Shown once the visitor has submitted OR a voice-driven send
  // resolves to sent/queued (R16 V4); the copy reflects the state.
  if (showSent) {
    const queued = resolved === 'queued'; // send deferred (Resend not yet provisioned)
    const inFlight = resolved === null;
    return (
      <div
        className="reveal flex flex-col items-center gap-4 py-8 text-center"
        data-testid="close-sent"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brass-soft ring-1 ring-brass/40">
          {inFlight ? (
            <span className="h-5 w-5 rounded-full border-2 border-brass border-t-transparent animate-spin" />
          ) : (
            <Icon name="check" size={22} className="text-brass" />
          )}
        </span>
        <p className="font-display text-lg text-ink" data-testid="close-sent-headline">
          {inFlight
            ? 'Sending…'
            : queued
              ? "We've got your details"
              : 'Your spec sheet is on its way'}
        </p>
        <p className="max-w-xs text-sm text-subtle leading-relaxed">
          {queued
            ? "We'll email your spec sheet as soon as sending is live, and the team has your selection and will follow up on availability and terms."
            : 'The team has your selection and will follow up with availability and terms. Thank you for visiting Godgift.'}
        </p>
        {/* R22 K1 — once the visitor SUBMITTED and the send resolved, the card auto-advances to the
            goodbye (submittedSend timer above); on a voice-driven send with no local submit it HOLDS
            until the server drives ui.scene:'end'. Done is always a manual peer either way. */}
        <button
          type="button"
          onClick={onDone}
          className="btn-ghost mt-1 px-6 py-2.5 text-sm rounded-full"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="reveal flex flex-col gap-5" data-testid="close-scene">
      {/* Read-only order recap — the visitor sees exactly what they confirmed. */}
      <div className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-xl text-ink">Order noted</h3>
          {lines.length > 0 && (
            <span className="text-xs text-subtle">
              {lines.length === 1 ? '1 piece' : `${lines.length} pieces`} &middot;{' '}
              {orderTotalUnits(lines)} units
            </span>
          )}
        </div>
        {lines.length > 0 && (
          <ul className="flex flex-col gap-2" data-testid="close-recap">
            {lines.map((line) => (
              <li
                key={line.sku}
                data-testid={`close-line-${line.sku}`}
                className="flex items-center gap-3 rounded-lg bg-surface ring-1 ring-line p-2.5"
              >
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-canvas/40 grid place-items-center">
                  {line.image ? (
                    <img
                      src={line.image}
                      alt={line.name}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    // R17 Q1 — graceful no-image thumbnail (never the raw SKU); the row prints the
                    // full name beside it, so the fill stays wordless.
                    <NoImageFill icon={iconForCategory(line.category)} variant="thumb" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  {/* R17 Q1 — real piece name, never a raw SKU (the order payload keeps line.sku). */}
                  <p className="truncate font-display text-sm text-ink">
                    {displayPieceName(line.name, line.category, line.sku)}
                  </p>
                  <div className="flex items-center gap-2">
                    {line.category && (
                      <span className="truncate text-[11px] uppercase tracking-wide text-subtle">
                        {line.category}
                      </span>
                    )}
                    {line.onStand && <OnStandBadge variant="inline" />}
                  </div>
                </div>
                <span className="shrink-0 text-sm tabular-nums text-subtle">×{line.qty}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Keyboard-first email capture + reusable card scan. */}
      <form className="flex flex-col gap-4" onSubmit={handleSend}>
        <div className="flex flex-col gap-1.5">
          <p className="font-display text-lg text-ink">Where shall I send your spec sheet?</p>
          <p className="text-sm text-subtle leading-relaxed">
            Add an email and I'll send your selection across; the team will have your details for
            follow-up.
          </p>
        </div>

        {/* R29 X2.1 — email field carved into the panel (`.field-inset`). R31 W2 — the per-field Done
            is now a COMMIT: it validates the email, flashes an inline ack tick, blurs (dismisses the
            kiosk keyboard; the R30 guard still releases), then advances to the phone field / Send. An
            invalid email surfaces a gentle inline hint (never silence). Focus lifts a brass ring on
            the carve (see .field-inset:focus-within). */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="close-email"
            className="text-xs font-medium uppercase tracking-wide text-subtle"
          >
            Email
          </label>
          <div className="field-inset flex items-center gap-2 pr-1.5">
            <input
              ref={emailInputRef}
              id="close-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              placeholder="ritika@gulfdecor.com"
              aria-invalid={hint?.field === 'email' || undefined}
              aria-describedby={hint?.field === 'email' ? 'close-email-hint' : undefined}
              // R30 V1/V2 — mirror + emit the debounced draft; focus arms the guard, blur releases it.
              onChange={(e) => {
                setEmail(e.target.value);
                handleDraft('email', e.target.value);
              }}
              onFocus={() => setFocusedField('email')}
              onBlur={handleFieldBlur}
              // R31 W2 / R32 X1 — Enter in the email field SUBMITS it (same path as Done), never blind.
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleFieldSubmit('email');
                }
              }}
              className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:outline-none"
            />
            <DoneChip
              field="email"
              state={emailSubmit.state}
              onClick={() => handleFieldSubmit('email')}
            />
          </div>
          {hint?.field === 'email' && (
            <p
              id="close-email-hint"
              role="alert"
              data-testid="close-email-hint"
              className="reveal-bubble px-1 text-[11px] text-terracotta leading-relaxed"
            >
              {hint.text}
            </p>
          )}
        </div>

        {/* R13 U4 — optional phone (a second contact point). Keyboard-first tel input; also filled
            from a card scan (the scan returns `phone`). Clearly optional — no guilt UI. R31 W2 — the
            per-field Done COMMITS: an empty optional phone is not an error (it just moves on), a typed
            phone acks + advances to Send (when the email is valid) or surfaces the email need. */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="close-phone"
            className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-subtle"
          >
            Phone
            <span className="text-[10px] font-normal normal-case tracking-normal text-faint">
              optional
            </span>
          </label>
          <div className="field-inset flex items-center gap-2 pr-1.5">
            <input
              ref={phoneInputRef}
              id="close-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              placeholder="+971 50 123 4567"
              // R30 V1/V2 — mirror to the store (existing) + emit the debounced draft; focus/blur guard.
              onChange={(e) => {
                onType('phone', e.target.value);
                handleDraft('phone', e.target.value);
              }}
              onFocus={() => setFocusedField('phone')}
              onBlur={handleFieldBlur}
              // R31 W2 / R32 X1 — Enter in the phone field SUBMITS it (same path as Done).
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleFieldSubmit('phone');
                }
              }}
              data-testid="close-phone-input"
              className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:outline-none"
            />
            <DoneChip
              field="phone"
              state={phoneSubmit.state}
              onClick={() => handleFieldSubmit('phone')}
            />
          </div>
          {hint?.field === 'phone' && (
            <p
              id="close-phone-hint"
              role="alert"
              data-testid="close-phone-hint"
              className="reveal-bubble px-1 text-[11px] text-terracotta leading-relaxed"
            >
              {hint.text}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            {/* R29 X2.3 — the "Email my spec sheet" send action is the headline commit on this card: a
                BRASS PILL (foil-gradient pressable, `.pill-brass`) so it's unmistakable when Gigi points
                at it (the Monica confusion). Larger label + brass through-line + lift/sink pressable.
                R32 X3 — once the email is Saved, `send-cue` pulses the pill (auto-settling brass-ring,
                reduced-motion → static ring) so a visitor who never taps Send SEES the path forward. */}
            <button
              ref={sendBtnRef}
              type="submit"
              data-testid="close-send-email-btn"
              disabled={email.trim() === ''}
              data-cued={pathForwardCued && emailSubmit.state === 'saved' ? '' : undefined}
              className={`pill-brass inline-flex items-center gap-2 px-6 py-3 text-sm font-display ${
                pathForwardCued && emailSubmit.state === 'saved' ? 'send-cue' : ''
              }`}
            >
              <Icon name="send" size={14} />
              Email my spec sheet
            </button>
            {/* R14 B6 — card scan now offers a Take photo / Photo library / Browse files source sheet. */}
            <CardScanSource
              onFile={(f) => void handleFile(f)}
              disabled={scanning}
              title="Scan your card from"
              onScanSurfaceChange={onScanSurfaceChange}
            >
              {({ open, disabled }) => (
                <button
                  type="button"
                  data-testid="close-scan-btn"
                  disabled={disabled}
                  onClick={open}
                  className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm disabled:opacity-50"
                >
                  <Icon name="camera" size={14} />
                  {scanning ? 'Reading…' : 'Scan card'}
                </button>
              )}
            </CardScanSource>
          </div>
          {/* R32 X3 — the path forward, made unmistakable once the email is Saved. A visitor who filled
              the box but never taps Send now SEES the next action spelled out (the dev-119 hang). The
              line is truthful: it points at the Send pill (contact is on file; the send is the next
              beat), never claims a send that hasn't happened (the R58 promise-guard discipline). */}
          {pathForwardCued && emailSubmit.state === 'saved' && (
            <p
              data-testid="close-path-forward"
              className="reveal-bubble px-1 text-[11px] text-subtle leading-relaxed"
            >
              <span className="text-brass">Saved.</span> Tap{' '}
              <span className="font-medium text-ink">Email my spec sheet</span> above and I&apos;ll
              send your selection across.
            </p>
          )}
        </div>
        <p className="text-[11px] text-faint leading-relaxed">
          Used only to send your spec sheet and follow up on this enquiry. No unsolicited
          communications.
        </p>
      </form>
    </div>
  );
}

/**
 * R32 X1/X2 — the per-field Done/Saving/Saved/retry pressable. Replaces R31's transient `.done-acked`
 * flash with a PERSISTENT state: `saved` holds a brass "Saved ✓" until the field value changes (the
 * host reverts it via `reconcileSubmitAck`); `pending` shows a soft "Saving…" while the submit is
 * in-flight; `error` shows an actionable "Tap to retry" (never a false Saved, never silence). The tick
 * uses `.done-acked` only for the reduced-motion-safe entrance flourish on `saved` — the STATE, not a
 * timer, drives it now. `data-testid`s are UNCHANGED (`close-<field>-done-btn`) so the harness/tests
 * bind by the same handle.
 */
function DoneChip({
  field,
  state,
  onClick,
}: {
  field: CloseField;
  state: SubmitAckState;
  onClick: () => void;
}) {
  const saved = state === 'saved';
  const pending = state === 'pending';
  const label = submitAckLabel(state);
  return (
    <button
      type="button"
      data-testid={`close-${field}-done-btn`}
      data-ack-state={state}
      onClick={onClick}
      disabled={pending}
      aria-label={field === 'email' ? 'Confirm email' : 'Confirm phone'}
      aria-live="polite"
      className={`soft-lift shrink-0 rounded-full px-3 py-1.5 text-xs ${
        saved
          ? 'done-acked bg-brass-soft text-brass'
          : state === 'error'
            ? 'bg-surface text-terracotta'
            : 'bg-surface text-subtle'
      } ${pending ? 'opacity-70' : ''}`}
    >
      {saved ? (
        <span className="inline-flex items-center gap-1">
          <Icon name="check" size={12} />
          {label}
        </span>
      ) : (
        label
      )}
    </button>
  );
}
